import { JobStatus } from "@/lib/queue/types";
import { create } from "zustand";

/*
Jobs store is used to track a BullMQ job's progress and status on the client
a job is added to the store after user triggers an action that creates a job
the job is removed from the store after the job has completed or failed.

All jobs are rendered inside the Jobs component (@see src/components/jobs/Jobs.tsx).
Inside the Jobs component, we resolve the component to render based on the componentId property of the job.

JobMeta is used by a JobProgress<Suffix> component (@see src/components/jobs/JobProgress.tsx).
The component is used to display the progress of a job in the UI.

For example, when user creates a portfolio from an exchange, a job is created and added to the store.
The JobProgress component will display the progress of the job in the UI.
*/

type JobId = string;

/**
 * Represents the different components of job progress within the application.
 *
 * The `JobProgressComponent` type can be one of the following string literals:
 * - `"default"`: Represents the default job progress component.
 * - `"portfolio-calculate-values"`: Represents the component for calculating portfolio values.
 * - `"create-exchange"`: Represents the component for creating an exchange.
 * - `"create-wallet"`: Represents the component for creating a wallet.
 * - `"sync-exchange"`: Represents the component for syncing an exchange.
 * - `"sync-wallet"`: Represents the component for syncing a wallet.
 */
type JobProgressComponent =
  | "default"
  | "portfolio-calculate-values"
  | `portfolio-create-${"exchange" | "wallet"}`
  | `portfolio-sync-${"exchange" | "wallet"}`;

export type JobMeta = {
  name: string;
  status: JobStatus;

  // This allows us to render the correct JobProgress component
  componentId: JobProgressComponent;
};

// Binance portfolio sync started
// <name> started
// <name> completed
// <name> failed

interface JobsStore {
  jobs: Map<JobId, JobMeta>;

  addJob: (id: JobId, meta: JobMeta) => void;
  removeJob: (id: JobId) => void;
}

export const useJobsStore = create<JobsStore>((set) => ({
  jobs: new Map(),
  addJob: (id: JobId, meta: JobMeta) => {
    set((state) => {
      const newJobs = new Map(state.jobs);
      newJobs.set(id, meta);
      return { jobs: newJobs };
    });
  },
  removeJob: (id: JobId) => {
    set((state) => {
      const newJobs = new Map(state.jobs);
      newJobs.delete(id);
      return { jobs: newJobs };
    });
  },
}));
